import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import Raven from 'raven-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import { debounce, isUndefined, omit } from 'ui-addon-upgrades/utils';
import withErrorBoundary from '../../ErrorBoundary';
import UIPricingPageRedirectButton from '../../button/UIPricingPageRedirectButton';
import { mapPurchaseMotionApiName } from '../../ums/utils';
import { getIsRetailPortal } from '../common/api/fetchRetail';
import { prefetchMeetingData } from '../common/api/meetingHelpers';
import { CONTACT_SALES_BUTTON } from '../common/constants/ErrorBoundaryNames';
import { TALK_TO_SALES_BUTTON } from '../common/constants/PurchaseMotionLocations';
import { getParsedProductPurchaseMotions } from '../common/data/purchaseMotionData/getParsedProductPurchaseMotions';
import { sourceKeys } from '../common/data/upgradeData/properties/sources';
import { getPropertyRepInfo } from '../common/data/upgradeData/transformers/getPropertyRepInfo';
import { getSource } from '../common/data/upgradeData/transformers/getPropertySource';
import { getIsFreePortalAndOwnedProducts } from '../common/eligibility/getIsFreePortal';
import { overrideAssignable, sequence, trackCommModalClose, trackContactSalesButtonClick, trackContactSalesButtonView } from '../contactSales/utils';
import { PRICING_PAGE_REDIRECT, TALK_TO_SALES, TALK_TO_SALES_FALLBACK } from '../purchaseMotions/PurchaseMotionTypes';
import { trackPurchaseMotionFailure } from '../utils/metricUtils';
import CommModalContainer from './CommModalContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MODAL_STATE = {
  CLOSED: 'CLOSED',
  LOADING: 'LOADING',
  OPEN: 'OPEN'
};
const ContactSalesButton = props => {
  const {
    onClick = () => {},
    onCancel = () => {},
    allowModal
  } = props;
  const isAssignable = overrideAssignable(props.isAssignable);
  const [ttsPurchaseMotion, setTtsPurchaseMotion] = useState(undefined);
  const [repInfoPromise, setRepInfoPromise] = useState(undefined);
  const [isFreePortalPromise, setIsFreePortalPromise] = useState(undefined);
  const [isRetailPortalPromise, setIsRetailPortalPromise] = useState(undefined);
  const [modalState, setModalState] = useState(MODAL_STATE.CLOSED);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const isFreePortal = useRef(undefined);
  const isRetailPortal = useRef(undefined);
  const repInfo = useRef(undefined);
  const isMounted = useRef(true);
  const {
    upgradeData
  } = props;
  const upgradeDataWithApiName = useMemo(() => {
    const {
      app,
      screen,
      uniqueId
    } = upgradeData;
    return Object.assign({}, upgradeData, {
      apiName: mapPurchaseMotionApiName(!upgradeData.apiName, upgradeData.upgradeProduct, 'ContactSalesButton', upgradeData.apiName),
      source: getSource({
        app,
        screen,
        uniqueId
      }, sourceKeys.CONTACT_SALES_BUTTON)
    });
  }, [upgradeData]);
  useEffect(() => {
    if (!hasTrackedView) {
      trackContactSalesButtonView(props);
      setHasTrackedView(true);
    }
    setRepInfoPromise(getPropertyRepInfo(upgradeDataWithApiName, allowModal));
  }, [props, hasTrackedView, upgradeDataWithApiName, allowModal]);
  useEffect(() => {
    if (isFreePortalPromise === undefined) {
      setIsFreePortalPromise(getIsFreePortalAndOwnedProducts());
    }
    if (isRetailPortalPromise === undefined) {
      setIsRetailPortalPromise(getIsRetailPortal());
    }
    prefetchMeetingData().catch(error => {
      Raven.captureException(new Error('Failed to prefetchMeetingData from UIContactSalesButton'), {
        extra: {
          error
        }
      });
    });
  }, [isFreePortalPromise, isRetailPortalPromise]);
  useEffect(() => {
    // Fetch {purchaseMotions} to compute {isAssignable} ONLY IF:
    // {isAssignable} prop was not passed (we need to compute the value) AND
    // {ttsPurchaseMotion} is undefined (we have not fetched before) AND
    // {apiName} is defined
    if (isUndefined(isAssignable) && isUndefined(ttsPurchaseMotion) && upgradeDataWithApiName.apiName) {
      getParsedProductPurchaseMotions({
        location: TALK_TO_SALES_BUTTON,
        apiName: upgradeDataWithApiName.apiName
      }).then(purchaseMotions => {
        // TALK_TO_SALES_BUTTON location only returns one purchase motion
        const purchaseMotion = purchaseMotions && purchaseMotions[0] || TALK_TO_SALES_FALLBACK;
        if (isMounted.current) {
          setTtsPurchaseMotion(purchaseMotion);
        }
      }).catch(() => {
        if (isMounted.current) {
          setTtsPurchaseMotion(TALK_TO_SALES_FALLBACK);
        }
      });
    }
    return function cleanup() {
      isMounted.current = false;
    };

    // should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, upgradeDataWithApiName.apiName]);
  const computedIsAssignable = useCallback(() => {
    return isAssignable || ttsPurchaseMotion === TALK_TO_SALES;
  }, [isAssignable, ttsPurchaseMotion]);
  const handleClick = useCallback(() => {
    setModalState(MODAL_STATE.LOADING);
    const {
      pointType,
      upgradeKey
    } = upgradeDataWithApiName;
    trackPotentialBannerInteraction(pointType, upgradeKey).catch(error => {
      Raven.captureException(new Error('Failed to submit interaction from UIContactSalesButton'), {
        extra: {
          message: error.message,
          uniqueId: upgradeDataWithApiName.uniqueId
        }
      });
    });
    Promise.all([repInfoPromise, isFreePortalPromise, isRetailPortalPromise]).then(([repInfoResponse, isFreePortalResponse, isRetailPortalResponse]) => {
      repInfo.current = repInfoResponse && repInfoResponse.repInfo ? repInfoResponse.repInfo : {};
      isFreePortal.current = isFreePortalResponse === null || isFreePortalResponse === void 0 ? void 0 : isFreePortalResponse.isFreePortal;
      isRetailPortal.current = isRetailPortalResponse;
    }).catch(() => {
      repInfo.current = {};
      isFreePortal.current = true;
      isRetailPortal.current = false;
      trackPurchaseMotionFailure({
        uniqueId: upgradeDataWithApiName.uniqueId,
        apiName: upgradeDataWithApiName.apiName,
        purchaseMotion: TALK_TO_SALES
      });
      Raven.captureException(new Error('Communication methods modal opened without setting data'));
    }).finally(() => {
      trackContactSalesButtonClick(Object.assign({}, props, {
        isAssignable: computedIsAssignable()
      }), Boolean(isRetailPortal.current), repInfo.current);
      setModalState(MODAL_STATE.OPEN);
    });
  }, [computedIsAssignable, isFreePortalPromise, isRetailPortalPromise, props, repInfoPromise, upgradeDataWithApiName]);
  const handleClose = () => {
    trackCommModalClose(upgradeDataWithApiName, repInfo.current);
    setModalState(MODAL_STATE.CLOSED);
    onCancel();
  };
  const renderButton = () => {
    const CLICK_DELAY = 50;
    const debouncedHandleClick = debounce(handleClick, CLICK_DELAY);
    const onClickSequence = sequence(debouncedHandleClick, onClick);
    const propsToPass = omit(props, ['usageLimitBannerData', 'forwardedRef', 'onClick', 'upgradeData', 'allowModal', 'isAssignable', 'termId', 'additionalUnitQuantity', 'isUpgradePoint', '_textOverride', '_overrideCommunicationMethodKey', '_buttonOverride', '_overrideModalHeaderText', 'modalRoot', 'canViewNewSeats', 'ctaStyles']);
    if (ttsPurchaseMotion === PRICING_PAGE_REDIRECT) {
      // A pp redirect motion will only be returned for users in Russia
      return /*#__PURE__*/_jsx(UIPricingPageRedirectButton, Object.assign({
        upgradeData: upgradeDataWithApiName,
        block: props.block
      }, propsToPass));
    } else if (props._buttonOverride) {
      return /*#__PURE__*/_jsx("div", Object.assign({
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-test-id": "contact-sales-button-override"
      }, propsToPass, {
        children: props._buttonOverride
      }));
    } else {
      return /*#__PURE__*/_jsx(LoadingButton, {
        loading: modalState === MODAL_STATE.LOADING,
        buttonRef: props.forwardedRef,
        onClick: onClickSequence,
        "data-selenium": props['data-selenium'] || 'contact-sales-button',
        "data-test-id": props['data-test-id'] || 'contact-sales-button',
        "data-selenium-id": props['data-selenium-id'] || 'contact-sales-button',
        size: props.size,
        use: props.use,
        block: props.block,
        children: props._textOverride || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.contactSalesButton.text"
        })
      });
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderButton(), modalState === MODAL_STATE.OPEN && /*#__PURE__*/_jsx(CommModalContainer, {
      handleClose: handleClose,
      isFreePortal: Boolean(isFreePortal.current),
      isRetailPortal: Boolean(isRetailPortal.current),
      isAssignable: computedIsAssignable(),
      modalRoot: props.modalRoot,
      upgradeData: upgradeDataWithApiName,
      repInfo: repInfo.current,
      _overrideCommunicationMethodKey: props._overrideCommunicationMethodKey,
      _overrideModalHeaderText: props._overrideModalHeaderText,
      isUpgradePoint: props.isUpgradePoint,
      _shouldBypassCommsModal: props._shouldBypassCommsModal
    })]
  });
};
export default withErrorBoundary(ContactSalesButton, {
  boundaryName: CONTACT_SALES_BUTTON
});